// client/src/components/paymentBylaws/PaymentBylawForm.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Divider
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import paymentBylawService from '../../services/paymentBylawService';
import facultyService from '../../services/facultyService';

const PaymentBylawForm = () => {
  const { t, i18n } = useTranslation();
  const { user, isAdmin } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const copyFromId = searchParams.get('copyFrom');
  const isEditMode = Boolean(id);
  const isCopyMode = Boolean(copyFromId);

  const [formData, setFormData] = useState({
    bylaw_type: 'faculty',
    faculty_id: '',
    copy_from_id: copyFromId || null
  });
  const [faculties, setFaculties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditMode || isCopyMode);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [bylawDetails, setBylawDetails] = useState(null);

  // Current language
  const lang = i18n.language;

  // Load faculties
  useEffect(() => {
    const loadFaculties = async () => {
      try {
        const response = await facultyService.getFaculties();
        setFaculties(response.data);
        
        // For faculty users, set the faculty_id automatically
        if (!isAdmin() && user.faculty_id && !isEditMode) {
          setFormData(prev => ({
            ...prev,
            faculty_id: user.faculty_id
          }));
        }
      } catch (err) {
        console.error('Error loading faculties', err);
      }
    };
    
    loadFaculties();
  }, [isAdmin, user.faculty_id, isEditMode]);

  // Load payment bylaw data if in edit mode or copy mode
  useEffect(() => {
    if (isEditMode || isCopyMode) {
      const loadPaymentBylaw = async () => {
        try {
          setFetchLoading(true);
          const targetId = isEditMode ? id : copyFromId;
          const response = await paymentBylawService.getPaymentBylaw(targetId);
          
          if (isEditMode) {
            // In edit mode, load the actual bylaw data
            setFormData({
              bylaw_type: response.data.bylaw_type,
              faculty_id: response.data.faculty_id || '',
              copy_from_id: null
            });
            setBylawDetails(response.data);
          } else if (isCopyMode) {
            // In copy mode, just set the form's type and faculty
            setFormData({
              bylaw_type: response.data.bylaw_type,
              faculty_id: response.data.faculty_id || '',
              copy_from_id: copyFromId
            });
            setBylawDetails({
              ...response.data,
              name: `${response.data.name} (${t('copy')})`,
              is_approved: false
            });
          }
        } catch (err) {
          setError(t('errorLoadingPaymentBylaw'));
          navigate('/payment-bylaws');
        } finally {
          setFetchLoading(false);
        }
      };
      
      loadPaymentBylaw();
    }
  }, [isEditMode, isCopyMode, id, copyFromId, navigate, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Reset faculty_id if type changes to university
    if (name === 'bylaw_type' && value === 'university') {
      setFormData({
        ...formData,
        [name]: value,
        faculty_id: ''
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    // Validate required fields
    if (formData.bylaw_type === 'faculty' && !formData.faculty_id) {
      setError(t('facultyRequired'));
      setLoading(false);
      return;
    }
    
    try {
      // Prepare submission data
      const submissionData = {
        bylaw_type: formData.bylaw_type,
        faculty_id: formData.bylaw_type === 'faculty' ? formData.faculty_id : null
      };
      


      if (copyFromId) {
        submissionData.source_bylaw_id = copyFromId;
      }

      if (isEditMode) {
        // Currently not much to update for bylaws
        // This would be expanded if bylaw had editable properties
        setSuccess(t('paymentBylawUpdatedSuccess'));
      } else {
        // Create new bylaw
        const response = await paymentBylawService.createPaymentBylaw(submissionData);
        setSuccess(t('paymentBylawCreatedSuccess'));
        
        // Redirect to the bylaws list after a delay
        setTimeout(() => {
          navigate('/payment-bylaws');
        }, 2000);
      }
    } catch (err) {
      setError(isEditMode ? t('errorUpdatingPaymentBylaw') : t('errorCreatingPaymentBylaw'));
    } finally {
      setLoading(false);
      window.scrollTo(0, 0);
    }
  };

  if (fetchLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {isEditMode 
            ? t('editPaymentBylaw') 
            : isCopyMode 
              ? t('copyPaymentBylaw') 
              : t('addPaymentBylaw')}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        {bylawDetails && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle1">{bylawDetails.name}</Typography>
            {isCopyMode && (
              <Typography variant="body2">
                {t('creatingCopyOf')}: {bylawDetails.name}
              </Typography>
            )}
            <Typography variant="body2">
              {t('expiration')}: {new Date(bylawDetails.expired_at).toLocaleDateString()}
            </Typography>
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={3}>
            {/* Bylaw Type Selection - only admins can create university-wide bylaws */}
            {isAdmin() && (
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">{t('bylawType')}</FormLabel>
                  <RadioGroup
                    row
                    name="bylaw_type"
                    value={formData.bylaw_type}
                    onChange={handleChange}
                    disabled={isEditMode}
                  >
                    <FormControlLabel 
                      value="university" 
                      control={<Radio />} 
                      label={t('university')} 
                      disabled={isEditMode}
                    />
                    <FormControlLabel 
                      value="faculty" 
                      control={<Radio />} 
                      label={t('faculty')} 
                      disabled={isEditMode}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            )}
            
            {/* Faculty Selection - only if type is faculty */}
            {formData.bylaw_type === 'faculty' && (
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="faculty-label">{t('faculty')}</InputLabel>
                  <Select
                    labelId="faculty-label"
                    id="faculty_id"
                    name="faculty_id"
                    value={formData.faculty_id}
                    label={t('faculty')}
                    onChange={handleChange}
                    disabled={(!isAdmin() && user.faculty_id) || isEditMode}
                  >
                    <MenuItem value="">{t('selectFaculty')}</MenuItem>
                    {faculties.map((faculty) => (
                      <MenuItem key={faculty.id} value={faculty.id}>
                        {faculty[`name_${lang}`]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            {isEditMode && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  {t('editBylawWarning')}
                </Alert>
              </Grid>
            )}
            
            {isCopyMode && (
              <Grid item xs={12}>
                <Alert severity="info">
                  {t('copyBylawInfo')}
                </Alert>
              </Grid>
            )}
          </Grid>
          
          <Box sx={{ 
            mt: 3, 
            display: 'flex',
            gap: 2,
            justifyContent: direction === 'ltr' ? 'flex-end' : 'flex-start'
          }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/payment-bylaws')}
            >
              {t('cancel')}
            </Button>
            {!isEditMode && (
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? t('saving') : t('save')}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default PaymentBylawForm;